import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';

// Function to get the API base URL (works in both development and production)
const getApiBaseUrl = () => {
  // In production, use relative URLs
  // In development, use the specified server URL
  return process.env.NODE_ENV === 'production' 
    ? '' 
    : 'http://localhost:3001';
};

function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [oobCode, setOobCode] = useState('');

  useEffect(() => {
    // Log the full URL for debugging
    console.log('Current URL:', window.location.href);
    console.log('Search params:', Object.fromEntries([...searchParams]));
    
    // CRITICAL: Immediately redirect to login for ANY Firebase auth action completion
    // This ensures user never sees the reset-password page after completing the process
    if (!(searchParams.get('mode') === 'resetPassword' && searchParams.get('oobCode'))) {
      console.log('Not in resetPassword mode with oobCode - redirecting to login');
      // Force navigate to login with short timeout to ensure it happens
      setTimeout(() => window.location.href = '/login', 100);
      return;
    }
    
    // Check if this is a redirect after successful password reset on Firebase page
    if (searchParams.get('mode') === 'action' && 
        searchParams.get('oobMode') === 'resetPassword' && 
        searchParams.get('apiKey')) {
      console.log('Detected redirect after Firebase password reset');
      // Redirect to login page
      navigate('/login');
      return;
    }
    
    // Check for oobCode directly first
    let code = searchParams.get('oobCode');
    
    // If not found, Firebase might include it as part of the mode=resetPassword flow
    if (!code) {
      code = searchParams.get('code');
    }
    
    // Also check URL hash for oobCode (Firebase sometimes puts it there)
    if (!code && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      code = hashParams.get('oobCode');
      console.log('Checking hash params:', Object.fromEntries([...hashParams]));
    }
    
    // Check for Firebase deep link parameters
    if (!code) {
      const deepLinkMatch = window.location.href.match(/oobCode=([^&]+)/);
      if (deepLinkMatch && deepLinkMatch[1]) {
        code = deepLinkMatch[1];
        console.log('Found oobCode in deep link:', code.substring(0, 5) + '...');
      }
    }
    
    // Also check apiKey parameter which often indicates a Firebase auth operation
    const apiKey = searchParams.get('apiKey');
    const mode = searchParams.get('mode');
    
    console.log('Found code:', code);
    console.log('Mode:', mode);
    console.log('API Key:', apiKey);
    console.log('Checking additional Firebase parameters:', 
      searchParams.get('continueUrl'), 
      searchParams.get('lang')
    );
    
    if (code) {
      setOobCode(code);
      console.log('Setting oobCode:', code);
    } else if (mode === 'resetPassword') {
      // Try to extract oobCode from other parameters if in reset password mode
      const potentialCode = searchParams.get('oobCode') || 
                           searchParams.get('code') || 
                           window.location.href.split('oobCode=')[1]?.split('&')[0];
      
      console.log('Potential code:', potentialCode);
      
      if (potentialCode) {
        setOobCode(potentialCode);
        console.log('Setting potential oobCode:', potentialCode);
      } else {
        setError('Invalid or missing reset code. Please check your email link again.');
      }
    } else {
      setError('Invalid or missing reset code. Please request a new password reset link.');
    }
  }, [searchParams, navigate]); // Added navigate dependency

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword !== confirmPassword) {
      return setError('Passwords do not match');
    }
    if (newPassword.length < 6) {
      return setError('Password should be at least 6 characters');
    }

    setLoading(true);

    try {
      const response = await fetch(getApiBaseUrl() + '/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newPassword, oobCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to reset password.');
      } else {
        setMessage(data.message || 'Password has been reset successfully. Redirecting to login...');
        // Redirect to login immediately
        navigate('/login');
      }
    } catch (err) {
      console.error("Password Reset Error:", err);
      setError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // If there's no oobCode, show the error screen
  if (!oobCode) {
    return (
      <div className="flex min-h-screen bg-[#111827] justify-center items-center p-4">
        <div className="w-full max-w-md bg-[#1f2937] p-8 rounded-lg shadow-xl text-center">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h2 className="text-2xl font-bold text-white mb-4">Password Reset Error</h2>
          <p className="text-gray-300 mb-6">{error || 'Invalid or missing reset code.'}</p>
          <p className="text-gray-400 mb-8 text-sm">This may happen if the link is expired, already used, or was not clicked directly from your email.</p>
          <Link to="/forgot-password" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[#374151]">
            Request New Reset Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#111827] justify-center items-center p-4">
      <div className="w-full max-w-md bg-[#1f2937] p-8 rounded-lg shadow-xl">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white">Reset Password</h2>
          <p className="text-gray-300 mt-2">Enter your new password</p>
        </div>

        {message && <div className="bg-green-500 text-white p-3 rounded mb-4 text-sm">{message}</div>}
        {error && <div className="bg-red-500 text-white p-3 rounded mb-4 text-sm">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1" htmlFor="new-password">
              New Password
            </label>
            <input
              id="new-password"
              type="password"
              className="w-full px-4 py-2 bg-[#4b5563] text-white border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1" htmlFor="confirm-password">
              Confirm New Password
            </label>
            <input
              id="confirm-password"
              type="password"
              className="w-full px-4 py-2 bg-[#4b5563] text-white border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          <div>
            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-[#374151] disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ResetPassword;